import { useQuery } from "@apollo/client/react";
import { GET_BUDGET_DATA_QUERY } from "@/hooks";
import { closeSnackbar, enqueueSnackbar, type SnackbarKey } from "notistack";
import { useTextDomainContext } from "@/app/providers";
import { useContext, useEffect, useRef } from "react";
import type { QueryBudgetDataArgs } from "@/types/graphql";

type BudgetDataVariables = Partial<QueryBudgetDataArgs>;

/**
 * Custom hook to fetch and format the budget data.
 * @param {object} variables - The variables to be passed to the query.
 * @param {Function} [onCompleted] - Callback when the query completes successfully.
 * @param {Function} [onError] - Callback when an error occurs during the query.
 */
export function useBudgetData(
  variables: BudgetDataVariables,
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const skip =
    !variables.customer ||
    !variables.group ||
    !variables.address ||
    !variables.fromDate ||
    !variables.toDate ||
    !variables.meterType;

  const { data, loading, error } = useQuery<any, BudgetDataVariables>(
    GET_BUDGET_DATA_QUERY,
    {
      variables,
      skip,
    },
  );

  const loadingRef = useRef(loading);

  // 🔹 Handle successful query completion
  useEffect(() => {
    if (data?.budgetData) {
      onCompleted(data.budgetData);
    }
  }, [data, onCompleted]);

  // 🔹 Handle query errors
  useEffect(() => {
    if (error) {
      onError(error);

      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error, onError]);

  // Keep ref in sync with latest loading state
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const loadingSnackbarKeyRef = useRef<SnackbarKey | null>(null);

  // 🔹 Show snackbar if loading takes too long and hide it when loading completes
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;

    if (loading) {
      intervalId = setInterval(() => {
        if (loadingRef.current && !loadingSnackbarKeyRef.current) {
          loadingSnackbarKeyRef.current = enqueueSnackbar(
            gettext("Loading and verifying your data. Please wait a moment."),
            {
              variant: "info",
              persist: true,
            },
          );
        }
      }, 30000); // every 30s
    } else {
      if (loadingSnackbarKeyRef.current) {
        closeSnackbar(loadingSnackbarKeyRef.current);
        loadingSnackbarKeyRef.current = null;
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (loadingSnackbarKeyRef.current) {
        closeSnackbar(loadingSnackbarKeyRef.current);
        loadingSnackbarKeyRef.current = null;
      }
    };
  }, [gettext, loading]);

  return {
    data: data?.budgetData?.data || [],
    total: data?.budgetData?.total || null,
    raw: data?.budgetData || null,
    loading,
  };
}
